import React from "react";
import { InteractionType } from "@azure/msal-browser";
import { useMsalAuthentication } from "@azure/msal-react";
import { Navigate, Route, Routes } from "react-router-dom";
import { CssBaseline, Box, useTheme, useMediaQuery } from "@mui/material";

import { SyncDocumentsWithDBStatusProvider } from "PdfDocumentGrid/hooks/SyncDocumentWithDbStatus.context";
import { DocumentActionsStatusProvider } from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import AuthenticatedTemplateManager from "utils/azureAD/AuthenticatedTemplateManager";
import { tryPersistWithoutPromtingUser } from "utils/storageManager";
import { loginRequest } from "utils/azureAD/authConfig";
import { Header, Sidebar, Main } from "Layouts";

import HomePage from "HomePage";
import FavoritesPage from "FavoritesPage";
import CategoriesPage from "CategoriesPage";
import AcknowledgementPage from "AcknowledgementPage";
import SettingsPage, { SettingsProvider } from "SettingsPage";
import "./App.scss";
import { queryClient } from "utils/reactQuery/reactQueryManager";
import { QueryClientProvider } from "react-query";

const drawerwidth = 70;

const App: React.FunctionComponent = () => {
  const theme = useTheme();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(true);
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { login, error } = useMsalAuthentication(
    InteractionType.Silent,
    loginRequest
  );

  const handleDrawerOpen = React.useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerClose = React.useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Handle login error or infinite loop on IOS
  React.useEffect(() => {
    if (error) {
      login(InteractionType.Redirect, loginRequest);
    }
  }, [error, login]);

  React.useEffect(() => {
    const persistStorageIfNeeded = async () => {
      await tryPersistWithoutPromtingUser();
    };

    persistStorageIfNeeded();
  }, []);

  React.useEffect(() => {
    setIsDrawerOpen(matches);
  }, [matches]);

  React.useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} ${
      process.env.REACT_APP_ENV ?? ""
    }`;
  }, []);

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <CssBaseline />
      <AuthenticatedTemplateManager>
        <SettingsProvider>
          <QueryClientProvider client={queryClient}>
            <Header
              open={isDrawerOpen}
              handleDrawerOpen={handleDrawerOpen}
              drawerwidth={drawerwidth}
            />
            <Sidebar
              open={isDrawerOpen}
              handleDrawerClose={handleDrawerClose}
              handleDrawerOpen={handleDrawerOpen}
              drawerwidth={drawerwidth}
            />
            <Main open={isDrawerOpen} drawerwidth={drawerwidth}>
              <DocumentActionsStatusProvider>
                <SyncDocumentsWithDBStatusProvider>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route path="/starred" element={<FavoritesPage />} />
                    <Route
                      path="/acknowledgement"
                      element={<AcknowledgementPage />}
                    />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </SyncDocumentsWithDBStatusProvider>
              </DocumentActionsStatusProvider>
            </Main>
          </QueryClientProvider>
        </SettingsProvider>
      </AuthenticatedTemplateManager>
    </Box>
  );
};

export default App;
